// components/Common/ImageUploader.jsx
import React, { useState, useRef } from 'react';
import { FiUpload, FiX, FiImage } from 'react-icons/fi';
import { validateImageFile } from '../../utils/validators';
import { toast } from 'react-hot-toast';
import { getImageUrl } from '../../utils/constants'; // Import the correct helper

const ImageUploader = ({ images = [], onUpload, onRemove, maxFiles = 5, subfolder = 'products' }) => {
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    validateAndUploadFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    validateAndUploadFiles(files);
    e.target.value = '';
  };

  const validateAndUploadFiles = (files) => {
    // Filter image files
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      toast.error('Please select image files only');
      return;
    }

    // Check total files limit
    if (images.length + imageFiles.length > maxFiles) {
      toast.error(`You can only upload up to ${maxFiles} images`);
      return;
    }

    // Validate each file
    const validFiles = [];
    for (const file of imageFiles) {
      const validation = validateImageFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        toast.error(`${file.name}: ${validation.error}`);
      }
    }

    if (validFiles.length > 0) {
      onUpload(validFiles);
      toast.success(`${validFiles.length} image(s) added`);
    }
  };

  const getImagePreviewUrl = (image) => {
    // If image has a url field (from existing images)
    if (image.url) return image.url;
    // If image has fullUrl field (from processed images)
    if (image.fullUrl) return image.fullUrl;
    // If image is a File object (new upload)
    if (image instanceof File) return URL.createObjectURL(image);
    // If image is a string path
    if (typeof image === 'string') return getImageUrl(image, subfolder);
    // If image has filepath (from database)
    if (image.filepath) return getImageUrl(image.filepath, subfolder);
    // Fallback
    return 'https://via.placeholder.com/300x300?text=No+Image';
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        onClick={() => fileInputRef.current.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          dragging 
            ? 'border-red-500 bg-red-50' 
            : 'border-gray-300 hover:border-red-400 hover:bg-gray-50'
        }`}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          multiple
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
          className="hidden"
        />
        <FiUpload className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-2 text-sm font-medium text-gray-700">
          Drag and drop images here, or click to select
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Maximum {maxFiles} files. PNG, JPG, GIF, WEBP up to 5MB each
        </p>
      </div>

      {/* Image Preview */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {images.map((image, index) => (
            <div key={image.id || index} className="relative group">
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
                <img
                  src={getImagePreviewUrl(image)}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-full object-cover"
                  onLoad={(e) => {
                    // Revoke object URL after load to free memory
                    if (image instanceof File) {
                      URL.revokeObjectURL(e.target.src);
                    }
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://via.placeholder.com/300x300/F3F4F6/9CA3AF?text=Error";
                  }}
                />
              </div>
              
              {/* Image Info */}
              {image instanceof File && (
                <p className="mt-1 text-xs text-gray-500 truncate">
                  {image.name}
                </p>
              )}
              
              {/* Remove Button */}
              <button
                type="button"
                onClick={() => onRemove(image)}
                className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                title="Remove image"
              >
                <FiX className="w-3 h-3" />
              </button>

              {/* Main Image Indicator */}
              {index === 0 && (
                <div className="absolute top-2 left-2">
                  <span className="px-2 py-1 bg-blue-500 text-white text-xs rounded-full flex items-center shadow-lg">
                    <FiImage className="w-3 h-3 mr-1" /> Main
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageUploader;